import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  Edit2,
  HardHat,
  Phone,
  MapPin,
  Building2,
  Briefcase,
  Clock,
  CheckCircle2,
  Calendar,
  IndianRupee,
  Wallet,
  Wrench,
  Layers,
  FileText,
  Users,
  CreditCard
} from 'lucide-react';
import { formatMoney } from '@vns/core';
import { useSubcontractorDetail } from '../lib/directory';
import Shell from '../components/Shell';
import { SubcontractorDrawer } from './Subcontractors';
import {
  StatusChip,
  EmptyState,
  TableSkeleton,
  ErrorState,
  formatDate,
  initials,
} from '../components/ui';

import { type LucideIcon } from 'lucide-react';

type Tab = 'attendance' | 'payments' | 'projects';

const TABS: Array<{ key: Tab; label: string; icon: LucideIcon }> = [
  { key: 'attendance', label: 'Attendance History', icon: HardHat },
  { key: 'payments', label: 'Payments Ledger', icon: Wallet },
  { key: 'projects', label: 'Projects Worked', icon: Briefcase },
];

export default function SubcontractorDetail() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('attendance');
  const [editing, setEditing] = useState(false);
  const { data, isLoading, error, refetch } = useSubcontractorDetail(id);

  return (
    <Shell
      title={data?.name ?? 'Sub-Contractor Profile'}
      crumb="Sub-Contractors"
      actions={
        data && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              className="btn btn-auto btn-sm"
              onClick={() => setEditing(true)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, borderRadius: 'var(--radius-sm)' }}
            >
              <Edit2 size={13} />
              <span>Edit Profile</span>
            </button>
          </div>
        )
      }
    >
      <button
        className="btn btn-auto btn-sm btn-ghost"
        style={{
          marginBottom: 16,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          color: 'var(--text-soft)',
          paddingLeft: 4,
        }}
        onClick={() => navigate('/subcontractors')}
      >
        <ArrowLeft size={14} />
        <span>Back to Sub-Contractors</span>
      </button>

      {isLoading ? (
        <div className="card"><TableSkeleton rows={4} cols={4} /></div>
      ) : error ? (
        <div className="card"><ErrorState error={error} onRetry={() => void refetch()} /></div>
      ) : !data ? (
        <div className="card"><EmptyState icon="hardhat" title="Sub-contractor not found" /></div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
        >
          {/* Subcontractor Identity Card */}
          <div
            className="card"
            style={{
              padding: '20px 24px',
              marginBottom: 20,
              background: 'var(--surface)',
              border: '1px solid var(--border)',
            }}
          >
            <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <div
                className="avatar-sq"
                style={{
                  width: 56,
                  height: 56,
                  fontSize: 18,
                  fontWeight: 700,
                  borderRadius: 'var(--radius-sm)',
                  background: 'linear-gradient(135deg, #eff6ff, #dbeafe)',
                  color: '#2563eb',
                  border: '1px solid #bfdbfe',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 6px rgba(37, 99, 235, 0.12)',
                  flexShrink: 0,
                }}
              >
                {initials(data.name)}
              </div>

              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0, color: 'var(--text)' }}>
                    {data.name}
                  </h2>
                  <span
                    className="chip chip-blue"
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      padding: '2px 8px',
                      borderRadius: 'var(--radius-xs)',
                    }}
                  >
                    <HardHat size={11} />
                    <span>Labour Contractor</span>
                  </span>
                </div>

                <div
                  style={{
                    display: 'flex',
                    gap: 16,
                    flexWrap: 'wrap',
                    marginTop: 10,
                    fontSize: 12.5,
                    color: 'var(--text-soft)',
                    alignItems: 'center',
                  }}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <Phone size={13} style={{ color: 'var(--text-faint)' }} />
                    <span style={{ fontFamily: 'var(--font-mono)' }}>{data.phone}</span>
                  </span>

                  {data.city && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <MapPin size={13} style={{ color: 'var(--text-faint)' }} />
                      <span>{data.city}</span>
                    </span>
                  )}

                  {data.gstNumber && (
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 5,
                        fontFamily: 'var(--font-mono)',
                        background: 'var(--surface-2)',
                        padding: '2px 8px',
                        borderRadius: 'var(--radius-xs)',
                        border: '1px solid var(--border)',
                        fontSize: 11.5,
                      }}
                    >
                      <CreditCard size={12} style={{ color: 'var(--text-faint)' }} />
                      <span>GST: <strong>{data.gstNumber}</strong></span>
                    </span>
                  )}

                  {data.bankName && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <Building2 size={13} style={{ color: 'var(--text-faint)' }} />
                      <span>Bank: {data.bankName}</span>
                    </span>
                  )}
                </div>

                {data.workTypes && data.workTypes.length > 0 && (
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 12, alignItems: 'center' }}>
                    <span style={{ fontSize: 11.5, color: 'var(--text-faint)', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <Wrench size={11} /> Specializations:
                    </span>
                    {data.workTypes.map((t) => (
                      <span
                        key={t}
                        style={{
                          fontSize: 11.5,
                          fontWeight: 500,
                          padding: '3px 9px',
                          borderRadius: 'var(--radius-xs)',
                          background: '#eff6ff',
                          color: '#1d4ed8',
                          border: '1px solid #bfdbfe',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                        }}
                      >
                        <Layers size={10} />
                        {t}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* KPI Row */}
          <div className="kpi-row" style={{ marginBottom: 20 }}>
            <div className="kpi">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <span className="lbl">Total Attendance Value</span>
                <div style={{ color: '#2563eb', background: '#eff6ff', border: '1px solid #dbeafe', padding: 5, borderRadius: 'var(--radius-xs)' }}>
                  <IndianRupee size={14} />
                </div>
              </div>
              <div className="val num" style={{ fontSize: 20 }}>{formatMoney(data.totalAttendancePaise)}</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-faint)', marginTop: 4 }}>Gross labour value rendered</div>
            </div>

            <div className="kpi">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <span className="lbl">Total Paid</span>
                <div style={{ color: '#16a34a', background: 'rgba(22, 163, 74, 0.1)', padding: 5, borderRadius: 'var(--radius-xs)' }}>
                  <CheckCircle2 size={14} />
                </div>
              </div>
              <div className="val num" style={{ fontSize: 20, color: '#16a34a' }}>{formatMoney(data.totalPaidPaise)}</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-faint)', marginTop: 4 }}>Settled contractor payouts</div>
            </div>

            <div className="kpi">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <span className="lbl">Pending Balance</span>
                <div
                  style={{
                    color: data.pendingPaise > 0 ? '#ea580c' : 'var(--text-faint)',
                    background: data.pendingPaise > 0 ? 'rgba(234, 88, 12, 0.1)' : 'var(--surface-muted)',
                    padding: 5,
                    borderRadius: 'var(--radius-xs)',
                  }}
                >
                  <Clock size={14} />
                </div>
              </div>
              <div
                className="val num"
                style={{
                  fontSize: 20,
                  color: data.pendingPaise > 0 ? '#ea580c' : 'var(--text-soft)',
                }}
              >
                {formatMoney(data.pendingPaise)}
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--text-faint)', marginTop: 4 }}>
                {data.pendingPaise > 0 ? 'Outstanding balance owed' : 'All accounts fully settled'}
              </div>
            </div>

            <div className="kpi">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <span className="lbl">Worker-Days Logged</span>
                <div style={{ color: 'var(--brand)', background: 'rgba(37, 99, 235, 0.1)', padding: 5, borderRadius: 'var(--radius-xs)' }}>
                  <Users size={14} />
                </div>
              </div>
              <div className="val num" style={{ fontSize: 20 }}>{data.totalWorkerDays.toLocaleString('en-IN')}</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-faint)', marginTop: 4 }}>Cumulated trade shifts logged</div>
            </div>
          </div>

          {/* Tabbed Ledgers */}
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div
              style={{
                display: 'flex',
                gap: 8,
                padding: '12px 18px',
                borderBottom: '1px solid var(--border)',
                background: 'var(--surface-muted)',
                overflowX: 'auto',
              }}
            >
              {TABS.map((t) => {
                const IconComp = t.icon;
                const active = tab === t.key;
                const count =
                  t.key === 'attendance'
                    ? data.attendance.length
                    : t.key === 'payments'
                    ? data.payments.length
                    : data.projects.length;

                return (
                  <button
                    key={t.key}
                    onClick={() => setTab(t.key)}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 7,
                      padding: '8px 14px',
                      fontSize: 13,
                      fontWeight: active ? 600 : 500,
                      cursor: 'pointer',
                      borderRadius: 'var(--radius-sm)',
                      background: active ? 'var(--surface)' : 'transparent',
                      color: active ? 'var(--text)' : 'var(--text-soft)',
                      border: active ? '1px solid var(--border)' : '1px solid transparent',
                      boxShadow: active ? '0 1px 3px rgba(0,0,0,0.04)' : 'none',
                      transition: 'all 0.15s ease',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <IconComp size={14} style={{ color: active ? 'var(--accent)' : 'var(--text-faint)' }} />
                    <span>{t.label}</span>
                    <span
                      style={{
                        fontSize: 11,
                        padding: '1px 6px',
                        borderRadius: 'var(--radius-xs)',
                        background: active ? 'rgba(249, 115, 22, 0.12)' : 'var(--surface)',
                        color: active ? 'var(--accent)' : 'var(--text-faint)',
                        border: '1px solid var(--border)',
                        fontWeight: 600,
                      }}
                    >
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="table-wrap">
              <AnimatePresence mode="wait">
                {tab === 'attendance' && (
                  <motion.div
                    key="attendance-table"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                  >
                    {!data.attendance.length ? (
                      <EmptyState
                        icon="hardhat"
                        title="No attendance records found"
                        hint="Labour visits and daily attendance entries for this contractor will appear here."
                      />
                    ) : (
                      <table>
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Project</th>
                            <th>Work Type</th>
                            <th>Workers</th>
                            <th>Rate / Worker</th>
                            <th>Total Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.attendance.map((a) => (
                            <tr
                              key={a.id}
                              className="clickable"
                              onClick={() => navigate(`/projects/${a.projectId}`)}
                            >
                              <td>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: 'var(--text-soft)' }}>
                                  <Calendar size={12} style={{ color: 'var(--text-faint)' }} />
                                  <span>{formatDate(a.date)}</span>
                                </span>
                              </td>
                              <td>
                                <span style={{ fontWeight: 600, color: 'var(--text)' }}>
                                  {a.projectName}
                                </span>
                              </td>
                              <td>
                                <span
                                  className="chip chip-gray"
                                  style={{
                                    fontSize: 11.5,
                                    borderRadius: 'var(--radius-xs)',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 4,
                                  }}
                                >
                                  <Wrench size={10} />
                                  <span>{a.workType}</span>
                                </span>
                              </td>
                              <td className="num">
                                <span
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 4,
                                    fontWeight: 600,
                                    color: 'var(--text)',
                                  }}
                                >
                                  <Users size={12} style={{ color: 'var(--text-faint)' }} />
                                  <span>{a.workerCount}</span>
                                </span>
                              </td>
                              <td className="num">
                                {a.ratePerWorkerPaise != null ? (
                                  <span style={{ color: 'var(--text-soft)' }}>
                                    {formatMoney(a.ratePerWorkerPaise)}
                                  </span>
                                ) : (
                                  <span style={{ color: 'var(--text-faint)' }}>—</span>
                                )}
                              </td>
                              <td className="num" style={{ fontWeight: 600, color: 'var(--text)' }}>
                                {formatMoney(a.totalAmountPaise)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr>
                            <td colSpan={3} style={{ fontWeight: 700 }}>Total Attendance</td>
                            <td className="num" style={{ fontWeight: 700 }}>
                              {data.attendance.reduce((s, a) => s + a.workerCount, 0)}
                            </td>
                            <td />
                            <td className="num" style={{ fontWeight: 700 }}>
                              {formatMoney(data.attendance.reduce((s, a) => s + a.totalAmountPaise, 0))}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    )}
                  </motion.div>
                )}

                {tab === 'payments' && (
                  <motion.div
                    key="payments-table"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                  >
                    {!data.payments.length ? (
                      <EmptyState
                        icon="wallet"
                        title="No payments recorded"
                        hint="Payment disbursements made to this sub-contractor will appear here."
                      />
                    ) : (
                      <table>
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Project</th>
                            <th>Amount Paid</th>
                            <th>Payment Mode</th>
                            <th>Running Balance</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.payments.map((p) => (
                            <tr key={p.id}>
                              <td>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: 'var(--text-soft)' }}>
                                  <Calendar size={12} style={{ color: 'var(--text-faint)' }} />
                                  <span>{formatDate(p.date)}</span>
                                </span>
                              </td>
                              <td>
                                <span style={{ fontWeight: 500, color: p.projectName ? 'var(--text)' : 'var(--text-faint)' }}>
                                  {p.projectName ?? '—'}
                                </span>
                              </td>
                              <td className="num" style={{ fontWeight: 700, color: '#16a34a' }}>
                                {formatMoney(p.amountPaise)}
                              </td>
                              <td>
                                <span
                                  style={{
                                    textTransform: 'uppercase',
                                    fontSize: 11,
                                    fontWeight: 600,
                                    padding: '2px 8px',
                                    borderRadius: 'var(--radius-xs)',
                                    background: 'var(--surface-muted)',
                                    border: '1px solid var(--border)',
                                    color: 'var(--text-soft)',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 4,
                                  }}
                                >
                                  <Wallet size={11} />
                                  <span>{p.mode}</span>
                                </span>
                              </td>
                              <td className="num" style={{ fontWeight: 600, color: 'var(--text)' }}>
                                {formatMoney(p.balancePaise)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr>
                            <td colSpan={2} style={{ fontWeight: 700 }}>Total Paid</td>
                            <td className="num" style={{ fontWeight: 700, color: '#16a34a' }}>
                              {formatMoney(data.payments.reduce((s, p) => s + p.amountPaise, 0))}
                            </td>
                            <td colSpan={2} />
                          </tr>
                        </tfoot>
                      </table>
                    )}
                  </motion.div>
                )}

                {tab === 'projects' && (
                  <motion.div
                    key="projects-table"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                  >
                    {!data.projects.length ? (
                      <EmptyState
                        icon="briefcase"
                        title="No projects recorded"
                        hint="Projects where this sub-contractor has deployed crew or rendered services will appear here."
                      />
                    ) : (
                      <table>
                        <thead>
                          <tr>
                            <th>Project Name</th>
                            <th>Client</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.projects.map((p) => (
                            <tr
                              key={p.id}
                              className="clickable"
                              onClick={() => navigate(`/projects/${p.id}`)}
                            >
                              <td>
                                <span style={{ fontWeight: 600, color: 'var(--text)' }}>
                                  {p.name}
                                </span>
                              </td>
                              <td>
                                <span style={{ color: 'var(--text-soft)' }}>
                                  {p.clientName}
                                </span>
                              </td>
                              <td>
                                <StatusChip value={p.status} />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Edit Drawer */}
          {editing && (
            <SubcontractorDrawer
              existing={{
                id: data.id,
                name: data.name,
                phone: data.phone,
                city: data.city,
                gst_number: data.gstNumber,
                workTypes: data.workTypes,
              }}
              onClose={() => {
                setEditing(false);
                void refetch();
              }}
            />
          )}
        </motion.div>
      )}
    </Shell>
  );
}
